/**
 * SYRINX-lite — the arp voice: PLUCK and STAB.
 *
 * Transferred from Chirp/trill.html, the SYRINX module. Kept: the two-oscillator
 * mix (saw + square/triangle), the lowpass with its filter envelope kick, the
 * amp envelope, and the per-note glide memory. Dropped: the phaser, chorus,
 * BPM-synced ping-pong echo, the drawbar wave and MIDI. The shared master bus
 * carries whatever space this instrument gets.
 *
 * Like the bass engine it takes an output AudioNode and never reaches for
 * ctx.destination.
 */

import { clamp, midiHz } from '../common/pitch';

export interface ArpParams {
  cutoff: number;  // Hz
  res: number;
  envAmt: number;  // filter kick, octaves
  a: number; d: number; s: number; r: number;
  mix: number;     // 0 = saw only, 1 = square only
  detune: number;  // cents between the pair
  glide: number;   // seconds; 0 = no portamento
}

export interface ArpVoicePatch {
  readonly label: string;
  readonly note: string;
  readonly params: ArpParams;
}

export const ARP_VOICES = {
  pluck: {
    label: 'PLUCK',
    note: 'Short, bright, decays out of the way — the arp reads as notes, not a pad.',
    params: { cutoff: 1800, res: 6, envAmt: 2.2, a: 0.002, d: 0.16, s: 0.0, r: 0.09, mix: 0.25, detune: 7, glide: 0 },
  },
  stab: {
    label: 'STAB',
    note: 'Saw and square, filter kicked open — gate fodder.',
    params: { cutoff: 900, res: 9, envAmt: 3.0, a: 0.004, d: 0.28, s: 0.12, r: 0.14, mix: 0.55, detune: 12, glide: 0 },
  },
} as const satisfies Record<string, ArpVoicePatch>;

export type ArpVoiceId = keyof typeof ARP_VOICES;
export const ARP_VOICE_IDS = Object.keys(ARP_VOICES) as ArpVoiceId[];

export interface ArpEngine {
  noteOn(midi: number, when: number, dur: number, vel?: number): void;
  setCutoff(hz: number): void;
  setRes(q: number): void;
  setVoice(id: ArpVoiceId): void;
  setLevel(v: number): void;
  readonly params: ArpParams;
  dispose(): void;
}

export function createArpEngine(
  ctx: BaseAudioContext,
  out: AudioNode,
  voice: ArpVoiceId = 'pluck',
  level = 0.75,
): ArpEngine {
  const P: ArpParams = { ...ARP_VOICES[voice].params };
  let lastMidi: number | null = null;

  const master = ctx.createGain();
  master.gain.value = level * level;
  const bus = ctx.createGain();
  bus.connect(master);
  master.connect(out);

  // Sounding voices, so gestural cutoff/res sweeps the note that is playing
  // rather than waiting for the next one. Same discipline as the bass engine.
  const live = new Set<BiquadFilterNode>();

  function noteOn(midi: number, when: number, dur: number, vel = 1): void {
    const f = ctx.createBiquadFilter();
    f.type = 'lowpass';
    f.Q.value = P.res;
    const fc = clamp(60, 14000, P.cutoff);
    f.frequency.setValueAtTime(Math.min(16000, fc * Math.pow(2, P.envAmt)), when);
    f.frequency.setTargetAtTime(fc, when + 0.002, Math.max(0.02, P.d / 2.5));

    const amp = ctx.createGain();
    const peak = 0.42 * vel;
    const gateEnd = when + Math.max(0.03, dur);
    amp.gain.setValueAtTime(0.0001, when);
    amp.gain.linearRampToValueAtTime(peak, when + Math.max(0.0015, P.a));
    amp.gain.setTargetAtTime(Math.max(0.0001, peak * P.s), when + Math.max(0.0015, P.a), Math.max(0.012, P.d / 3));
    amp.gain.setTargetAtTime(0.0001, gateEnd, Math.max(0.012, P.r / 4));
    f.connect(amp); amp.connect(bus);

    const target = midiHz(midi);
    const from = P.glide > 0 && lastMidi !== null ? midiHz(lastMidi) : target;
    const stopAt = gateEnd + P.r * 3 + 0.06;
    const pair: OscillatorNode[] = [];
    for (let i = 0; i < 2; i++) {
      const o = ctx.createOscillator();
      o.type = i === 0 ? 'sawtooth' : 'square';
      o.detune.value = i === 0 ? -P.detune / 2 : P.detune / 2;
      o.frequency.setValueAtTime(from, when);
      if (from !== target) o.frequency.exponentialRampToValueAtTime(target, when + Math.max(0.005, P.glide));
      const g = ctx.createGain();
      g.gain.value = (i === 0 ? 1 - P.mix : P.mix) * 0.8;
      o.connect(g); g.connect(f);
      o.start(when); o.stop(stopAt);
      pair.push(o);
    }
    lastMidi = midi;
    live.add(f);
    pair[0].onended = () => {
      live.delete(f);
      try { amp.disconnect(); f.disconnect(); } catch { /* torn down */ }
    };
  }

  return {
    noteOn,
    params: P,
    setCutoff(hz) {
      const fc = clamp(60, 14000, hz);
      // Unchanged values leave each note's filter-envelope kick alone; only a
      // moving cutoff takes over the sounding voices (see gular-lite).
      if (Math.abs(fc - P.cutoff) < P.cutoff * 0.01) { P.cutoff = fc; return; }
      P.cutoff = fc;
      const now = ctx.currentTime;
      for (const f of live) f.frequency.setTargetAtTime(fc, now, 0.03);
    },
    setRes(q) {
      const res = clamp(0.5, 20, q);
      if (Math.abs(res - P.res) < 0.05) { P.res = res; return; }
      P.res = res;
      const now = ctx.currentTime;
      for (const f of live) f.Q.setTargetAtTime(res, now, 0.03);
    },
    setVoice(id) { Object.assign(P, ARP_VOICES[id].params); },
    setLevel(v) { master.gain.setTargetAtTime(v * v, ctx.currentTime, 0.02); },
    dispose() {
      live.clear();
      master.gain.setTargetAtTime(0.0001, ctx.currentTime, 0.02); // no click on scene switch
      setTimeout(() => { try { master.disconnect(); } catch { /* gone */ } }, 120);
    },
  };
}
